import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { numToPrice } from "../utils";
import { countViewerTrophies, pluralize } from "../../utils/seasonAwards";

const SLIDE_MS = 4000;

function buildSlides(data) {
  const { season, leagueName, totals, champion, awards, viewer } = data;
  const slides = [];

  if (totals) {
    slides.push({
      key: "rounds",
      kicker: `Season ${season}`,
      headline: (
        <>You played <em>{pluralize(totals.rounds, "round")}</em></>
      ),
      body: `${leagueName}, ${pluralize(totals.matches, "match", "matches")}, ${pluralize(totals.goals, "goal")}.`,
    });
  }

  if (champion) {
    slides.push({
      key: "champion",
      big: "1st",
      headline: viewer?.isChampion ? (
        <>You are the <em>champions</em></>
      ) : (
        <>{champion.teamName} are <em>champions</em></>
      ),
      body: `${pluralize(champion.points, "point")}. W${champion.wins} D${champion.draws} L${champion.losses}.`,
    });
  }

  if (awards?.goldenBoot) {
    slides.push({
      key: "goldenBoot",
      kicker: "Golden Boot",
      headline: (
        <>{awards.goldenBoot.name}, <em>{awards.goldenBoot.goals} goals</em></>
      ),
      body: `Top scorer of the season, playing for ${awards.goldenBoot.teamName}.`,
    });
  }

  if (awards?.signingOfSeason) {
    slides.push({
      key: "signing",
      kicker: "Signing of the Season",
      headline: (
        <>{awards.signingOfSeason.name} repaid the <em>{numToPrice(awards.signingOfSeason.fee)}</em></>
      ),
      body: `${awards.signingOfSeason.goalsSinceJoining} goals since joining ${awards.signingOfSeason.teamName}.`,
    });
  }

  const trophies = countViewerTrophies(viewer);
  slides.push({
    key: "cabinet",
    kicker: "The cabinet",
    headline: (
      <><em>{trophies} {trophies === 1 ? "trophy" : "trophies"}</em> added</>
    ),
    body: "They stay on your team page. Share the wrap from the cabinet.",
  });

  return slides;
}

/**
 * Tap-through "Season Wrapped" story overlay. Auto-advances every 4s,
 * tap anywhere advances, X or Escape closes. Finishing the last slide
 * closes the story and fires a success toast.
 */
export function SeasonWrappedStory({ data, onClose }) {
  const slides = useMemo(() => buildSlides(data), [data]);
  const [index, setIndex] = useState(0);

  const handleAdvance = useCallback(() => {
    if (index >= slides.length - 1) {
      toast.success("That was your season. Share it from the cabinet.");
      onClose();
      return;
    }
    setIndex(index + 1);
  }, [index, slides.length, onClose]);

  useEffect(() => {
    const timer = setTimeout(handleAdvance, SLIDE_MS);
    return () => clearTimeout(timer);
  }, [handleAdvance]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const slide = slides[index];
  if (!slide) return null;

  return (
    <div
      className="wrapped-story"
      role="dialog"
      aria-modal="true"
      aria-label="Season Wrapped"
      onClick={handleAdvance}
    >
      <div className="wrapped-bars" aria-hidden="true">
        {slides.map((item, i) => (
          <i
            key={item.key}
            className={i < index ? "done" : i === index ? "now" : ""}
          />
        ))}
      </div>
      <button
        type="button"
        className="wrapped-close"
        aria-label="Close Season Wrapped"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        &#10005;
      </button>
      <div className="wrapped-slide" key={slide.key}>
        {slide.big ? (
          <div className="wrapped-big">{slide.big}</div>
        ) : (
          <div className="wrapped-kicker">{slide.kicker}</div>
        )}
        <h3 className="wrapped-headline">{slide.headline}</h3>
        <p className="wrapped-body">{slide.body}</p>
      </div>
      <div className="wrapped-tapnote">Tap to continue</div>
    </div>
  );
}
